import { useState } from 'react';

import { Inputs } from '@/components/form/Inputs';
import { Modal } from '@/components/Modal';
import { RemovableDashedTag } from '@/components/RemovableDashedTag';

export const AddTag = ({
  open,
  onClose,
  disabled,
  description = 'Tags help organize and classify assets in your attack surface.',
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  disabled: boolean;
  description?: string;
  onSubmit: (tag: string) => void;
}) => {
  const [tagValue, setTagValue] = useState('');

  function reset() {
    setTagValue('');
    onClose();
  }

  return (
    <Modal
      title="Add Custom Tag"
      open={open}
      onClose={onClose}
      footer={{
        text: 'Add',
        form: 'custom-tag-form',
        disabled,
      }}
    >
      <p className="text-chariot-primary mb-10">{description}</p>
      <div className="flex">
        <div className="flex grow flex-col justify-start">
          <form
            id="custom-tag-form"
            className="space-y-4"
            onSubmit={async event => {
              event.preventDefault();
              await onSubmit(tagValue);
              reset();
            }}
          >
            <Inputs
              inputs={[
                {
                  label: 'Tag',
                  value: tagValue,
                  placeholder: 'production',
                  name: 'tag',
                  required: true,
                },
              ]}
              onChange={values => setTagValue(String(values.tag || ''))}
            />
          </form>
        </div>
      </div>
    </Modal>
  );
};

export const SimpleTags = ({
  tags,
  onRemove,
  variant = 'default',
}: {
  tags: string[];
  onRemove?: (tag: string) => void;
  variant?: 'default' | 'compact';
}) => {
  if (tags.length === 0) return null;

  // Compact variant for drawer headers - smaller, solid Chariot purple
  if (variant === 'compact') {
    return (
      <div
        className="flex flex-wrap gap-2"
        role="list"
        aria-label="Custom tags"
      >
        {tags?.map((tag, index) =>
          onRemove ? (
            <RemovableDashedTag
              key={`${tag}-${index}`}
              label={tag}
              size="xs"
              onRemove={() => onRemove(tag)}
              className="border-chariot-brand-primary bg-chariot-brand-primary hover:bg-chariot-brand-primary/80 hover:border-chariot-brand-primary/80 border-solid text-white"
            />
          ) : (
            <span
              key={`${tag}-${index}`}
              role="listitem"
              className="border-chariot-brand-primary bg-chariot-brand-primary inline-flex items-center rounded-full border-2 border-solid px-2 py-1 text-[10px] leading-3 font-semibold text-white uppercase"
            >
              {tag}
            </span>
          )
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className="flex flex-wrap gap-2" role="list" aria-label="Custom tags">
      {tags?.map((tag, index) =>
        onRemove ? (
          <RemovableDashedTag
            key={`${tag}-${index}`}
            label={tag}
            size="sm"
            onRemove={() => onRemove(tag)}
          />
        ) : (
          <span
            key={`${tag}-${index}`}
            role="listitem"
            className="border-chariot-stroke text-chariot-text-secondary inline-flex items-center rounded-full border-2 border-dashed px-3 py-1.5 text-xs leading-3 font-semibold uppercase"
          >
            {tag}
          </span>
        )
      )}
    </div>
  );
};
